import { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Callout, Disclosure, OptionBuyingStrategySelect } from '@/components/UI';
import { useOptionBuyingStrategies } from '@/hooks/useOptionBuyingStrategies';
import { formatOptionBuyingStrategyMeta } from '@utils/optionBuyingStrategyHelpers';

// Match the backend OptionStrategyOverrides interface
export interface OptionStrategyOverrides {
    /** Experiment-wide default strategy ID */
    default?: number;
    /** Duration-specific overrides (applies to all horizons for that duration) */
    byDuration?: Record<string, number>;
    /** Duration+Horizon specific overrides (highest priority) */
    byCombo?: Record<string, number>;
}

interface Props {
    durations: string[];
    value: OptionStrategyOverrides | null;
    onChange: (overrides: OptionStrategyOverrides | null) => void;
    disabled?: boolean;
}

interface OverrideEntry {
    id: string;
    type: 'default' | 'duration' | 'combo';
    duration?: string;
    horizon?: string;
    strategyId?: number;
}

// Common durations and their typical horizons
const DURATION_HORIZONS: Record<string, string[]> = {
    '1m': ['5m', '15m', '30m', '1h'],
    '5m': ['15m', '30m', '1h', '2h'],
    '15m': ['30m', '1h', '2h', '4h'],
    '30m': ['1h', '2h', '4h', '1d'],
    '1h': ['2h', '4h', '1d', '2d'],
    '4h': ['1d', '2d', '1w', '2w'],
    '1d': ['2d', '1w', '2w', '1M'],
    '1w': ['2w', '1M', '2M', '3M']
};

export function OptionStrategyOverrideEditor({ durations, value, onChange, disabled = false }: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [entries, setEntries] = useState<OverrideEntry[]>([]);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Fetch available option strategies
    const { error: strategiesError, isLoading: strategiesLoading, strategiesById } = useOptionBuyingStrategies();

    // Convert overrides to entries for editing
    useEffect(() => {
        const newEntries: OverrideEntry[] = [];

        if (value?.default) {
            newEntries.push({
                id: 'default',
                type: 'default',
                strategyId: value.default
            });
        }

        if (value?.byDuration) {
            Object.entries(value.byDuration).forEach(([duration, strategyId]) => {
                newEntries.push({
                    id: `duration_${duration}`,
                    type: 'duration',
                    duration,
                    strategyId
                });
            });
        }

        if (value?.byCombo) {
            Object.entries(value.byCombo).forEach(([combo, strategyId]) => {
                const [duration, horizon] = combo.split('_');

                if (duration && horizon) {
                    newEntries.push({
                        id: `combo_${combo}`,
                        type: 'combo',
                        duration,
                        horizon,
                        strategyId
                    });
                }
            });
        }

        setEntries(newEntries);
        setIsExpanded(newEntries.length > 0);
        setShowAdvanced(newEntries.some((e) => e.type === 'combo'));
    }, [value]);

    // Convert entries back to overrides format
    const updateOverrides = (newEntries: OverrideEntry[]) => {
        const overrides: OptionStrategyOverrides = {};
        let hasAnyOverride = false;

        newEntries.forEach((entry) => {
            if (!entry.strategyId) return;

            if (entry.type === 'default') {
                overrides.default = entry.strategyId;
                hasAnyOverride = true;
            } else if (entry.type === 'duration' && entry.duration) {
                if (!overrides.byDuration) overrides.byDuration = {};
                overrides.byDuration[entry.duration] = entry.strategyId;
                hasAnyOverride = true;
            } else if (entry.type === 'combo' && entry.duration && entry.horizon) {
                if (!overrides.byCombo) overrides.byCombo = {};
                overrides.byCombo[`${entry.duration}_${entry.horizon}`] = entry.strategyId;
                hasAnyOverride = true;
            }
        });

        onChange(hasAnyOverride ? overrides : null);
    };

    const addEntry = (type: OverrideEntry['type']) => {
        const newEntry: OverrideEntry = {
            id: `${type}_${Date.now()}`,
            type
        };

        if (type === 'duration' && durations.length > 0) {
            newEntry.duration = durations[0];
        } else if (type === 'combo' && durations.length > 0) {
            const firstDuration = durations[0];
            newEntry.duration = firstDuration;
            const horizons = DURATION_HORIZONS[firstDuration];

            if (horizons && horizons.length > 0) {
                newEntry.horizon = horizons[0];
            }
        }

        const newEntries = [...entries, newEntry];
        setEntries(newEntries);
        updateOverrides(newEntries);
    };

    const removeEntry = (id: string) => {
        const newEntries = entries.filter((e) => e.id !== id);
        setEntries(newEntries);
        updateOverrides(newEntries);
    };

    const updateEntry = (id: string, updates: Partial<OverrideEntry>) => {
        const newEntries = entries.map((e) => (e.id === id ? { ...e, ...updates } : e));

        setEntries(newEntries);
        updateOverrides(newEntries);
    };

    const getStrategyDescription = (strategyId?: number) => {
        if (!strategyId) return null;
        const strategy = strategiesById.get(strategyId);
        if (!strategy) return null;

        return formatOptionBuyingStrategyMeta(strategy);
    };

    if (strategiesLoading) {
        return (
            <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-4">
                <div className="text-sm text-zinc-400">Loading option strategies...</div>
            </div>
        );
    }

    if (strategiesError) {
        return (
            <div className="rounded-lg border border-red-900 bg-red-950/20 p-4">
                <div className="text-sm text-red-400">Failed to load option strategies: {strategiesError.message}</div>
            </div>
        );
    }

    return (
        <Disclosure
            badge={
                entries.length > 0 ? (
                    <span className="rounded-full bg-blue-900/50 px-2 py-0.5 text-xs font-medium text-blue-400">
                        {entries.length} override{entries.length !== 1 ? 's' : ''}
                    </span>
                ) : undefined
            }
            disabled={disabled}
            expanded={isExpanded}
            title="Option Strategy Overrides"
            variant="card"
            onToggle={setIsExpanded}
        >
            <div className="space-y-4">
                {/* Info message */}
                <Callout title="Intelligent Strategy Selection" variant="info">
                    The system resolves an option buying strategy for each duration and horizon. Its configured DTE, pricing, and spread
                    rules apply to both stocks and indexes. Use overrides to select a different buying strategy.
                </Callout>

                {/* Default override */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-zinc-300">Default Strategy</h4>
                        {!entries.some((e) => e.type === 'default') && (
                            <button
                                className="flex items-center space-x-1 rounded-md px-2 py-1 text-xs font-medium text-blue-400 hover:bg-blue-950/50 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={disabled}
                                type="button"
                                onClick={() => addEntry('default')}
                            >
                                <FaPlus className="h-3 w-3" />
                                <span>Add Default</span>
                            </button>
                        )}
                    </div>

                    {entries
                        .filter((e) => e.type === 'default')
                        .map((entry) => (
                            <div className="flex items-center space-x-2" key={entry.id}>
                                <OptionBuyingStrategySelect
                                    noMargin
                                    className="flex-1"
                                    isDisabled={disabled}
                                    mode="single"
                                    placeholder="Select default strategy..."
                                    size="sm"
                                    value={entry.strategyId ?? null}
                                    onChange={(strategyId) => updateEntry(entry.id, { strategyId: strategyId ?? undefined })}
                                />
                                <button
                                    aria-label="Remove override entry"
                                    className="rounded-md p-1 text-zinc-400 hover:bg-zinc-700 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-50"
                                    disabled={disabled}
                                    type="button"
                                    onClick={() => removeEntry(entry.id)}
                                >
                                    <FaTimes className="h-4 w-4" />
                                </button>
                            </div>
                        ))}

                    {entries.find((e) => e.type === 'default' && e.strategyId) && (
                        <div className="ml-2 text-xs text-zinc-500">
                            {getStrategyDescription(entries.find((e) => e.type === 'default')?.strategyId)}
                        </div>
                    )}
                </div>

                {/* Duration-specific overrides */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-zinc-300">Duration-Specific Overrides</h4>
                        <button
                            className="flex items-center space-x-1 rounded-md px-2 py-1 text-xs font-medium text-blue-400 hover:bg-blue-950/50 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={disabled || durations.length === 0}
                            type="button"
                            onClick={() => addEntry('duration')}
                        >
                            <FaPlus className="h-3 w-3" />
                            <span>Add Duration</span>
                        </button>
                    </div>

                    {entries
                        .filter((e) => e.type === 'duration')
                        .map((entry) => (
                            <div className="space-y-1" key={entry.id}>
                                <div className="flex items-center space-x-2">
                                    <select
                                        className="w-24 rounded-md border border-zinc-700 bg-zinc-800 px-2 py-2 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-zinc-900 disabled:text-zinc-500"
                                        disabled={disabled}
                                        value={entry.duration ?? ''}
                                        onChange={(e) => updateEntry(entry.id, { duration: e.target.value })}
                                    >
                                        {durations.map((duration) => (
                                            <option key={duration} value={duration}>
                                                {duration}
                                            </option>
                                        ))}
                                    </select>
                                    <OptionBuyingStrategySelect
                                        noMargin
                                        className="flex-1"
                                        isDisabled={disabled}
                                        mode="single"
                                        placeholder="Select strategy..."
                                        size="sm"
                                        value={entry.strategyId ?? null}
                                        onChange={(strategyId) => updateEntry(entry.id, { strategyId: strategyId ?? undefined })}
                                    />
                                    <button
                                        aria-label="Remove override entry"
                                        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                        disabled={disabled}
                                        type="button"
                                        onClick={() => removeEntry(entry.id)}
                                    >
                                        <FaTimes className="h-4 w-4" />
                                    </button>
                                </div>
                                {entry.strategyId && (
                                    <div className="ml-2 text-xs text-zinc-500">{getStrategyDescription(entry.strategyId)}</div>
                                )}
                            </div>
                        ))}
                </div>

                {/* Advanced: Duration+Horizon specific overrides */}
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <button
                            className="flex items-center space-x-1 text-sm font-medium text-zinc-300 hover:text-white"
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                        >
                            {showAdvanced ? <FaChevronUp className="h-3 w-3" /> : <FaChevronDown className="h-3 w-3" />}
                            <span>Advanced: Duration + Horizon Overrides</span>
                        </button>
                        {showAdvanced && (
                            <button
                                className="flex items-center space-x-1 rounded-md px-2 py-1 text-xs font-medium text-blue-400 hover:bg-blue-950/50 disabled:cursor-not-allowed disabled:opacity-50"
                                disabled={disabled || durations.length === 0}
                                type="button"
                                onClick={() => addEntry('combo')}
                            >
                                <FaPlus className="h-3 w-3" />
                                <span>Add Combo</span>
                            </button>
                        )}
                    </div>

                    {showAdvanced &&
                        entries
                            .filter((e) => e.type === 'combo')
                            .map((entry) => (
                                <div className="space-y-1" key={entry.id}>
                                    <div className="flex items-center space-x-2">
                                        <select
                                            className="w-20 rounded-md border border-zinc-700 bg-zinc-800 px-2 py-2 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-zinc-900 disabled:text-zinc-500"
                                            disabled={disabled}
                                            value={entry.duration ?? ''}
                                            onChange={(e) => {
                                                const newDuration = e.target.value;
                                                const horizons = DURATION_HORIZONS[newDuration];

                                                updateEntry(entry.id, {
                                                    duration: newDuration,
                                                    horizon: horizons?.[0] ?? entry.horizon
                                                });
                                            }}
                                        >
                                            {durations.map((duration) => (
                                                <option key={duration} value={duration}>
                                                    {duration}
                                                </option>
                                            ))}
                                        </select>
                                        <span className="text-sm text-zinc-500">+</span>
                                        <select
                                            className="w-20 rounded-md border border-zinc-700 bg-zinc-800 px-2 py-2 text-sm text-white focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-zinc-900 disabled:text-zinc-500"
                                            disabled={disabled}
                                            value={entry.horizon ?? ''}
                                            onChange={(e) => updateEntry(entry.id, { horizon: e.target.value })}
                                        >
                                            {(DURATION_HORIZONS[entry.duration ?? ''] ?? ['1h', '2h', '4h', '1d']).map((horizon) => (
                                                <option key={horizon} value={horizon}>
                                                    {horizon}
                                                </option>
                                            ))}
                                        </select>
                                        <OptionBuyingStrategySelect
                                            noMargin
                                            className="flex-1"
                                            isDisabled={disabled}
                                            mode="single"
                                            placeholder="Select strategy..."
                                            size="sm"
                                            value={entry.strategyId ?? null}
                                            onChange={(strategyId) => updateEntry(entry.id, { strategyId: strategyId ?? undefined })}
                                        />
                                        <button
                                            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                            disabled={disabled}
                                            type="button"
                                            onClick={() => removeEntry(entry.id)}
                                        >
                                            <FaTimes className="h-4 w-4" />
                                        </button>
                                    </div>
                                    {entry.strategyId && (
                                        <div className="ml-2 text-xs text-zinc-500">{getStrategyDescription(entry.strategyId)}</div>
                                    )}
                                </div>
                            ))}
                </div>
            </div>
        </Disclosure>
    );
}
